<template>
  <toggle-section
    section="SmallBusinessAttributes"
    chevron-position="right"
    :has-padding="false"
    :is-open="isOpen"
    @toggle="isOpen = !isOpen"
  >
    <template #titleSection>
      <div class="flex items-center justify-start space-x-2">
        <lf-h4>
          {{
            $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SMALL_BUSINESS_ATTRIBUTES")
          }}
        </lf-h4>
        <counter-pill class="h-6 w-6" :count="count" :color="COLORS[9]" />
      </div>
    </template>
    <template #content>
      <div class="space-y-6 p-6">
        <template
          v-for="(record, index) in attributesGroup.AttributesGroup"
          :key="index"
        >
          <toggle-section
            v-for="section in sections"
            :key="section.title"
            class="collapse-toggle-padding"
            :section="section.title"
            chevron-position="right"
            :has-padding="section.padded ?? false"
            :content-gray="section.padded ?? false"
            :is-open="openedAttributes[index][section.title]"
            :data-cy="section.title"
            @toggle="$emit('toggle', { index, section: section.title })"
          >
            <template #titleSection>
              <div class="flex items-center justify-start space-x-2">
                <lf-h4>
                  {{ section.title }}
                </lf-h4>
                <counter-pill
                  class="h-6 w-6"
                  :count="getSectionCount(section)"
                  :color="COLORS[9]"
                />
              </div>
            </template>
            <template #content>
              <div class="flex items-start rounded-sm bg-white py-4">
                <div class="w-1/2 border-r">
                  <div
                    v-for="column in section.columns.slice(
                      0,
                      Math.ceil(section.columns.length / 2)
                    )"
                    :key="column.field"
                  >
                    <div class="grid grid-cols-2 justify-between px-4 py-2">
                      <div>{{ column.title }}</div>
                      <div class="text-right text-headline">
                        {{
                          getValueForField(record, column.field, column.boolean)
                        }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="w-1/2">
                  <div
                    v-for="column in section.columns.slice(
                      Math.ceil(section.columns.length / 2),
                      section.columns.length
                    )"
                    :key="column.field"
                  >
                    <div class="grid grid-cols-2 justify-between px-4 py-2">
                      <div>{{ column.title }}</div>
                      <div class="text-right text-headline">
                        {{
                          getValueForField(record, column.field, column.boolean)
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-for="(tileSection, index) in section.tileSections"
                :key="index"
                class="mt-6 flex flex-wrap rounded-sm bg-white"
              >
                <div
                  v-for="tile in tileSection"
                  :key="tile.field"
                  :class="[section.tileClass, tile.class]"
                  class="flex flex-col items-start space-y-2 border-divider p-4"
                >
                  <div>{{ tile.title }}</div>
                  <div class="flex items-center space-x-2">
                    <span class="text-lg font-semibold text-headline">
                      {{ getValueForField(record, tile.field) }}
                    </span>
                    <lf-pill size="sm" color-index="9">
                      {{ tile.pillText }}
                    </lf-pill>
                  </div>
                </div>
              </div>
            </template>
          </toggle-section>
        </template>
      </div>
    </template>
  </toggle-section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { COLORS } from "@/helpers/constants";

import CounterPill from "@/components/CounterPill.vue";
import ToggleSection from "@/components/ToggleSection.vue";

import type {
  AttributesGroup,
  AttributesGroups,
} from "@/models/commercialData/lexisnexis/LexisNexisSmallBusinessBlendedCreditScore";

const emit = defineEmits<{
  toggle: [{ index: number; section: string }];
  "all-sections:init": [string[]];
}>();
const props = defineProps<{
  attributesGroup: AttributesGroups;
  openedAttributes: Record<string, boolean>[];
}>();

const { t } = useI18n();

type TileSection = {
  title: string;
  field: string;
  pillText: string;
  class?: string;
}[];

type Section = {
  title: string;
  columns: {
    title: string;
    field: string;
    boolean?: boolean;
  }[];
  tileSections?: TileSection[];
  padded?: boolean;
  tileClass?: string;
};

const isOpen = ref(true);

const sections = computed<Section[]>(() => {
  return [
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_INFO"),
      columns: [
        {
          title: t("COMMON.BUSINESS_NAME"),
          field: "OutBestBusName",
        },
        {
          title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS"),
          field: "OutBestBusStreetAddr",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.CITY")}`,
          field: "OutBestBusCity",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.STATE")}`,
          field: "OutBestBusState",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.ZIP_CODE")}`,
          field: "OutBestBusZip",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.TIN")}`,
          field: "OutBestBusTIN",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.PHONE")}`,
          field: "OutBestBusPhone",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.NAICS")}`,
          field: "OutBestNAICS",
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.SIC")}`,
          field: "OutBestSIC",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.VERIFICATION_OF_BUSINESS_INPUT_PHONE"
          ),
          field: "VerificationBusInputPhone",
          boolean: true,
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.VERIFICATION_OF_BUSINESS_INPUT_NAME"
          ),
          field: "VerificationBusInputName",
          boolean: true,
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.VERIFICATION_OF_BUSINESS_INPUT_ADDRESS"
          ),
          field: "VerificationBusInputAddr",
          boolean: true,
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.VERIFICATION_OF_BUSINESS_INPUT_TIN"
          ),
          field: "VerificationBusInputTIN",
          boolean: true,
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.VERIFICATION_OF_BUSINESS_INPUT_INDUSTRY"
          ),
          field: "VerificationBusInputIndustry",
          boolean: true,
        },
      ],
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INPUT_DATA_CHECK"),
      columns: [
        {
          title: t("COMMON.BUSINESS_NAME"),
          field: "InputCheckBusName",
          boolean: true,
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_ALTERNATE_NAME"),
          field: "InputCheckBusAltName",
          boolean: true,
        },
        {
          title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS"),
          field: "InputCheckBusAddr",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.CITY")}`,
          field: "InputCheckBusCity",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.STATE")}`,
          field: "InputCheckBusState",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.ZIP_CODE")}`,
          field: "InputCheckBusZip",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.TIN")}`,
          field: "InputCheckBusTIN",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.PHONE")}`,
          field: "InputCheckBusPhone",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.FAX")}`,
          field: "InputCheckBusFax",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.NAICS")}`,
          field: "InputCheckBusNAICS",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.SIC")}`,
          field: "InputCheckBusSIC",
          boolean: true,
        },
        {
          title: `${t("COMMON.BUSINESS")} ${t("COMMON.AGE")}`,
          field: "InputCheckBusAge",
          boolean: true,
        },
        ...getIndexedRepColumns(1),
        ...getIndexedRepColumns(2),
        ...getIndexedRepColumns(3),
      ],
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_ATTRIBUTES"),
      columns: [
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_ADDRESS_COUNT"),
          field: "BusinessAddrCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_RECORD_TIME_OLDEST"
          ),
          field: "BusinessRecordTimeOldest",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_RECORD_TIME_NEWEST"
          ),
          field: "BusinessRecordTimeNewest",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_RECORD_UPDATED_12M"
          ),
          field: "BusinessRecordUpdated12M",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.NON_PROFIT_FIRM"),
          field: "FirmNonProfit",
          boolean: true,
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.IRS_RETIRMENT_PLAN_FIRM"),
          field: "FirmIRSRetirementPlan",
          boolean: true,
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.OBSERVED_FIRM_AGE"),
          field: "FirmAgeObserved",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.EMPLOYEE_COUNT"),
          field: "FirmEmployeeCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.EMPLOYEE_RANGE_COUNT"),
          field: "FirmEmployeeRangeCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.FIRM_OWNERSHIP_TYPE"),
          field: "FirmOwnershipType",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REPORTED_SALES"),
          field: "FirmReportedSales",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REPORTED_SALES_RANGE"),
          field: "FirmReportedSalesRange",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REPORTED_EARNINGS"),
          field: "FirmReportedEarnings",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PARENT_COMPANY_INDICATOR"
          ),
          field: "FirmParentCompanyInd",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LOCATION_COUNT"),
          field: "OrgLocationCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.RELATED_ENTITY_COUNT"),
          field: "OrgRelatedCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LEGAL_ENTITY_COUNT"),
          field: "OrgLegalEntityCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ADDRESS_LEGAL_ENTITY_COUNT"
          ),
          field: "OrgAddrLegalEntityCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SINGLE_LOCATION"),
          field: "OrgSingleLocation",
          boolean: true,
        },
      ],
      tileSections: [
        [
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_ACTIVITY"),
            field: "BusinessActivity03M",
            pillText: `3 ${t("COMMON.MONTHS")}`,
            class: "border-r",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_ACTIVITY"),
            field: "BusinessActivity06M",
            pillText: `6 ${t("COMMON.MONTHS")}`,
            class: "border-r",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_ACTIVITY"),
            field: "BusinessActivity12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
          },
        ],
      ],
      padded: true,
      tileClass: "w-1/3",
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SECRETARY_OF_STATE"),
      columns: [
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_AGENT_CHANGE_TIME"),
          field: "SOSTimeAgentChange",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_FOREIGN_STATE_FLAG"),
          field: "SOSForeignStateFlag",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_INCORPORATION_FILINGS_COUNT"
          ),
          field: "SOSIncorporationFilingsCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_INCORPORATION_TIME_OLDEST"
          ),
          field: "SOSIncorporationTimeOldest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_STATE_COUNT"),
          field: "SOSStateCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_STANDING_BEST"),
          field: "SOSStandingBest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SOS_STANDING_DEFUNCT"),
          field: "SOSStandingDefunct",
        },
      ],
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LEGAL_DATA"),
      columns: [
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BANKRUPTCY_COUNT"),
          field: "BankruptcyCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BANKRUPTCY_CHAPTER"),
          field: "BankruptcyChapter",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BANKRUPTCY_TIME_NEWEST"),
          field: "BankruptcyTimeNewest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_COUNT"),
          field: "LienCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_TOTAL_AMOUNT"),
          field: "LienTotalAmount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_NEWEST_TYPE"),
          field: "LienNewestType",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_TIME_OLDEST"),
          field: "LienTimeOldest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_TIME_NEWEST"),
          field: "LienTimeNewest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_FED_TAX_COUNT"),
          field: "LienFedTaxCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_FED_TAX_TOTAL_AMOUNT"
          ),
          field: "LienFedTaxTotalAmount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_FORECLOSURE_COUNT"),
          field: "LienForeclosureCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_FORECLOSURE_TOTAL_AMOUNT"
          ),
          field: "LienForeclosureTotalAmount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_COUNT"),
          field: "JudgmentCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_TOTAL_AMOUNT"),
          field: "JudgmentTotalAmount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_NEWEST_TYPE"),
          field: "JudgmentNewestType",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_TIME_OLDEST"),
          field: "JudgmentTimeOldest",
        },
      ],
      tileSections: [
        [
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BANKRUPTCY_COUNT"),
            field: "BankruptcyCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-r w-1/3",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BANKRUPTCY_COUNT"),
            field: "BankruptcyCount24M",
            pillText: `24 ${t("COMMON.MONTHS")}`,
            class: "border-r w-1/3",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BANKRUPTCY_COUNT"),
            field: "BankruptcyCount84M",
            pillText: `84 ${t("COMMON.MONTHS")}`,
            class: "w-1/3",
          },
        ],
        [
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_COUNT"),
            field: "LienCount03M",
            pillText: `3 ${t("COMMON.MONTHS")}`,
            class: "border-r border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_COUNT"),
            field: "LienCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_COUNT"),
            field: "LienCount24M",
            pillText: `24 ${t("COMMON.MONTHS")}`,
            class: "w-1/2 border-r",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.LIEN_COUNT"),
            field: "LienCount36M",
            pillText: `36 ${t("COMMON.MONTHS")}`,
            class: "w-1/2",
          },
        ],
        [
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_COUNT"),
            field: "JudgmentCount03M",
            pillText: `3 ${t("COMMON.MONTHS")}`,
            class: "border-r border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_COUNT"),
            field: "JudgmentCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_COUNT"),
            field: "JudgmentCount24M",
            pillText: `24 ${t("COMMON.MONTHS")}`,
            class: "w-1/2 border-r",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.JUDGMENT_COUNT"),
            field: "JudgmentCount36M",
            pillText: `36 ${t("COMMON.MONTHS")}`,
            class: "w-1/2",
          },
        ],
      ],
      padded: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSET_DATA"),
      columns: [
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_COUNT_EVER"),
          field: "AssetPropCountEver",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_COUNT_CURRENT"),
          field: "AssetPropCountCurrent",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_STATE_COUNT_CURRENT"
          ),
          field: "AssetPropStateCountCurrent",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_LOT_SIZE_TOTAL_EVER"
          ),
          field: "AssetPropLotSizeTotalEver",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_LOT_SIZE_TOTAL_CURRENT"
          ),
          field: "AssetPropLotSizeTotalCurrent",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_SQUARE_FOOTAGE_TOTAL_EVER"
          ),
          field: "AssetPropSqFootageTotalEver",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_SQUARE_FOOTAGE_TOTAL_CURRENT"
          ),
          field: "AssetPropSqFootageTotalCurrent",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_ASSESSED_TOTAL_EVER"
          ),
          field: "AssetPropAssessedTotalEver",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_ASSESSED_TOTAL_CURRENT"
          ),
          field: "AssetPropAssessedTotalCurrent",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.AIRCRAFT_COUNT"),
          field: "AssetAircraftCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.WATERCRAFT_COUNT"),
          field: "AssetWatercraftCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UCC_COUNT"),
          field: "UCCCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UCC_TIME_OLDEST"),
          field: "UCCTimeOldest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UCC_TIME_NEWEST"),
          field: "UCCTimeNewest",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UCC_ACTIVE_COUNT"),
          field: "UCCActiveCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UCC_ROLES"),
          field: "UCCRoles",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UCC_ROLES_ACTIVE"),
          field: "UCCRolesActive",
        },
      ],
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRIES"),
      columns: [
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_CONSUMER_ADDRESS"
          ),
          field: "InquiryConsumerAddress",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_CONSUMER_PHONE"),
          field: "InquiryConsumerPhone",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_CONSUMER_ADDRESS_SSN"
          ),
          field: "InquiryConsumerAddressSSN",
        },
      ],
      tileSections: [
        [
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_COUNT"),
            field: "InquiryCount03M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-r border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_COUNT"),
            field: "InquiryCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_CREDIT_COUNT"),
            field: "InquiryCreditCount03M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-r border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_CREDIT_COUNT"),
            field: "InquiryCreditCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: " border-b w-1/2",
          },
          {
            title: t(
              "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_HIGH_RISK_COUNT"
            ),
            field: "InquiryHighRiskCount03M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-r border-b w-1/2",
          },
          {
            title: t(
              "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_HIGH_RISK_COUNT"
            ),
            field: "InquiryHighRiskCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-b w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_OTHER_COUNT"),
            field: "InquiryOtherCount03M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "border-r w-1/2",
          },
          {
            title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_OTHER_COUNT"),
            field: "InquiryOtherCount12M",
            pillText: `12 ${t("COMMON.MONTHS")}`,
            class: "w-1/2",
          },
        ],
      ],
      padded: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.EXECUTIVE_LINKS"),
      columns: [
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_NAME_ON_FILE"),
          field: "BusExecLinkRepNameOnFile",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_ADDRESS_ON_FILE"),
          field: "BusExecLinkRepAddrOnFile",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_PHONE_ON_FILE"),
          field: "BusExecLinkRepPhoneOnFile",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_SSN_ON_FILE"),
          field: "BusExecLinkRepSSNOnFile",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_NAME_REP_FIRST_NAME"
          ),
          field: "BusExecLinkBusNameRepFirst",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_NAME_REP_FULL_NAME"
          ),
          field: "BusExecLinkBusNameRepFull",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_ADDRESS_BUSINESS_ADDRESS"
          ),
          field: "BusExecLinkRepAddrBusAddr",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_PHONE_BUSINESS_PHONE"
          ),
          field: "BusExecLinkRepPhoneBusPhone",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REP_SSN_BUSINESS_TIN"),
          field: "BusExecLinkRepSSNBusTIN",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.UTILITY_OVERLAP_COUNT"),
          field: "BusExecLinkUtilOverlapCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.INQUIRY_OVERLAP_COUNT"),
          field: "BusExecLinkInqOverlapCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.PROPERTY_OVERLAP_COUNT"),
          field: "BusExecLinkPropOverlapCount",
        },
      ],
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATIONS"),
      columns: [
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_COUNT"),
          field: "AssociateCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_BUSINESS_COUNT"
          ),
          field: "AssociateBusinessCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_FELONY_COUNT"),
          field: "AssociateFelonyCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_COUNT_WITH_FELONY"
          ),
          field: "AssociateCountWithFelony",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_BANKRUPT_COUNT"
          ),
          field: "AssociateBankruptCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_COUNT_WITH_BANKRUPTCY"
          ),
          field: "AssociateCountWithBankruptcy",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_BANKRUPT_12M_COUNT"
          ),
          field: "AssociateBankrupt12MCount",
        },
        {
          title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_LIEN_COUNT"),
          field: "AssociateLienCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_JUDGMENT_COUNT"
          ),
          field: "AssociateJudgmentCount",
        },
        {
          title: t(
            "DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.ASSOCIATE_WATCHLIST_COUNT"
          ),
          field: "AssociateWatchlistCount",
        },
      ],
    },
  ];
});

const count = computed(() => {
  const sections = props.attributesGroup?.AttributesGroup;
  if (!sections) {
    return 0;
  }
  return sections.reduce((acc, section) => {
    return acc + section.Attributes.Attribute.length;
  }, 0);
});

const getTileSectionsTilesCount = (sections: TileSection[] | undefined) => {
  if (!sections) {
    return 0;
  }
  return sections.reduce((acc, section) => {
    return acc + section.length;
  }, 0);
};

const getSectionCount = (section: Section) => {
  return (
    section.columns.length + getTileSectionsTilesCount(section.tileSections)
  );
};

const getIndexedRepColumns = (index: number) => {
  const prefix = `InputCheckAuthRep${index > 1 ? index : ""}`;
  return [
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_FIRST_NAME", {
        count: index,
      }),
      field: `${prefix}FirstName`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_MIDDLE_NAME", {
        count: index,
      }),
      field: `${prefix}MiddleName`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_LAST_NAME", {
        count: index,
      }),
      field: `${prefix}LastName`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_ADDRESS", {
        count: index,
      }),
      field: `${prefix}Addr`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_CITY", {
        count: index,
      }),
      field: `${prefix}City`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_STATE", {
        count: index,
      }),
      field: `${prefix}State`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_ZIP", {
        count: index,
      }),
      field: `${prefix}Zip`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_SSN", {
        count: index,
      }),
      field: `${prefix}SSN`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_PHONE", {
        count: index,
      }),
      field: `${prefix}Phone`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_AGE", {
        count: index,
      }),
      field: `${prefix}Age`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_DOB", {
        count: index,
      }),
      field: `${prefix}DOB`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_DL", {
        count: index,
      }),
      field: `${prefix}DL`,
      boolean: true,
    },
    {
      title: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BUSINESS_REP_DL_STATE", {
        count: index,
      }),
      field: `${prefix}DLState`,
      boolean: true,
    },
  ];
};

const getValueForField = (
  record: AttributesGroup,
  field: string,
  boolean = false
) => {
  const value = record.Attributes.Attribute.find(
    (attribute) => attribute.Name === field
  )?.Value;
  if (!value || value === "-1") {
    return "-";
  }
  if (["0", "1", 0, 1].includes(value) && boolean) {
    return Boolean(Number(value)).toString();
  }
  return value;
};

emit(
  "all-sections:init",
  sections.value.map((section) => section.title)
);
</script>
